import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Tableau dense (components/DataTable), API de `Table` shadcn/ui.
 * Lignes 36px, texte 13px, en-tête collant sur `muted`, survol `accent`,
 * colonnes de montants en mono alignées à droite (`kind="num"`).
 * Dans une `Card`, le tableau n'a pas de contour ; hors carte, `framed`.
 */

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  /** Contour + rayon + fond `card` (`dt-wrap` hors d'une carte). */
  framed?: boolean;
  /** Lignes de 44px (`is-comfortable`) : préférence d'affichage, pas un défaut. */
  comfortable?: boolean;
  /** Classes du conteneur défilant. */
  wrapperClassName?: string;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, framed = false, comfortable = false, wrapperClassName, ...props }, ref) => (
    <div
      className={cn(
        'relative w-full overflow-auto',
        framed && 'rounded-lg border border-border bg-card',
        wrapperClassName,
      )}
    >
      <table
        ref={ref}
        data-density={comfortable ? 'comfortable' : 'dense'}
        className={cn(
          'w-full border-separate border-spacing-0 text-[13px] leading-[18px] text-card-foreground',
          'group/table',
          className,
        )}
        {...props}
      />
    </div>
  ),
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <thead ref={ref} className={className} {...props} />);
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&>tr:last-child>td]:border-b-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

/** Ligne de total (`tfoot`) : fond `muted`, graisse 500. */
const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      '[&>tr>td]:h-9 [&>tr>td]:border-t [&>tr>td]:border-border [&>tr>td]:bg-muted [&>tr>td]:px-3 [&>tr>td]:font-medium',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /** Ligne sélectionnée (`is-selected`) : fond `accent`. */
  selected?: boolean;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, selected = false, ...props }, ref) => (
    <tr
      ref={ref}
      data-state={selected ? 'selected' : undefined}
      className={cn(
        '[tbody>&:hover>td]:bg-accent [&[data-state=selected]>td]:bg-accent',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        props.onClick !== undefined && 'cursor-pointer',
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

/**
 * Nature de la colonne, commune à `TableHead` et `TableCell` :
 * - `name` : colonne du nom (gras 500), première colonne ;
 * - `num` : montant / quantité, mono tabulaire, à droite ;
 * - `mono` : identifiant, date courte ;
 * - `sub` : texte secondaire `muted-foreground` ;
 * - `act` : colonne d'actions (menu « … »), à droite, 44px ;
 * - `check` : colonne de sélection, 40px.
 */
const cellKind = cva('', {
  variants: {
    kind: {
      text: '',
      name: 'font-medium',
      num: 'whitespace-nowrap text-right font-mono tabular-nums',
      mono: 'font-mono tabular-nums',
      sub: 'text-muted-foreground',
      act: 'w-11 whitespace-nowrap pr-2 text-right',
      check: 'w-10',
    },
  },
  defaultVariants: { kind: 'text' },
});

export type CellKind = NonNullable<VariantProps<typeof cellKind>['kind']>;

interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  kind?: CellKind;
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, kind, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'sticky top-0 z-[1] h-8 whitespace-nowrap border-b border-border bg-muted px-3 text-left align-middle text-label text-muted-foreground',
        cellKind({ kind }),
        kind === 'name' && 'font-medium',
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = 'TableHead';

interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  kind?: CellKind;
}

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, kind, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        'h-9 border-b border-border px-3 align-middle group-data-[density=comfortable]/table:h-11',
        cellKind({ kind }),
        className,
      )}
      {...props}
    />
  ),
);
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-3 text-caption text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

interface Cell2Props extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Ligne principale (texte ou lien `rowLinkClass`). */
  primary: React.ReactNode;
  /** Ligne secondaire en caption `muted-foreground`. */
  secondary?: React.ReactNode;
  /** Ligne principale en mono (clé, référence). */
  primaryMono?: boolean;
  /** Ligne secondaire en mono. */
  secondaryMono?: boolean;
}

/** `cell-2` : cellule sur deux lignes (nom + email, clé + description). */
function Cell2({
  primary,
  secondary,
  primaryMono = false,
  secondaryMono = false,
  className,
  ...props
}: Cell2Props): React.JSX.Element {
  return (
    <span className={cn('grid min-w-0 leading-4', className)} {...props}>
      <span className={cn('truncate', primaryMono && 'font-mono')}>{primary}</span>
      {secondary !== undefined && secondary !== null ? (
        <small
          className={cn(
            'truncate text-caption font-normal text-muted-foreground',
            secondaryMono && 'font-mono',
          )}
        >
          {secondary}
        </small>
      ) : null}
    </span>
  );
}

/** `row-link` : lien de la cellule principale qui ouvre la fiche (à poser sur `<Link>`). */
const rowLinkBase =
  'rounded-sm text-inherit no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
const rowLinkClass = `${rowLinkBase} font-medium`;

/** Variante de `row-link` pour une référence technique (mono, 12px, graisse normale). */
const rowLinkMonoClass = `${rowLinkBase} font-mono text-xs font-normal`;

interface TableEmptyRowProps {
  colSpan: number;
  children: React.ReactNode;
}

/** Ligne unique « aucun résultat » dans un `<TableBody>`. Pour un état vide riche, `EmptyState`. */
function TableEmptyRow({ colSpan, children }: TableEmptyRowProps): React.JSX.Element {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-[13px] text-muted-foreground">
        {children}
      </td>
    </tr>
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableEmptyRow,
  Cell2,
  rowLinkClass,
  rowLinkMonoClass,
};
